// src/utils/carteleria/detalleProyeccionCarteleria.js
//
// F3c — Proyección de ventas, MODAL de detalle (hardware + cartelería): parte numérica extraída de
// `VentasProyeccion/index.js` (handleOpenModal). Misma entrada que usaba el bloque y mismo resultado: la lista de
// ítems (hardware primero, cartelería después) que la página concatena a las demás verticales.
//
// "Histórico congelado + motor": el modal abre UN mes (start === end). Si el mes es anterior a CORTE_HISTORICO
// (2026-10) → `detalleModalCarteleriaHistorico` (copia literal del código anterior). Desde el corte → motor
// `calendarioNegocio`, con SOLO estos cambios permitidos respecto de antes:
//   · D12  — hardware con tramo único sin `monto`: vale `monto_hardware`.
//   · fin de contrato / "el estado manda" (D13) — facturación de cartelería: la ventana del modal es la del
//     motor (último mes de servicio, sin el mes de más con inicio a mitad de mes; activo vencido: hasta el mes actual).
//   · ventana de contenidos — la facturación incluye contenidos solo dentro de contenidos_fecha_inicio/fin.

import { calendarioNegocio, devengoDelMes } from "./calendarioNegocio";
import {
  ESTADOS_CIERRE,
  componentesCierreCarteleria,
  plazoContenidosPropio,
  totalCierreBaseLocal,
} from "./cierresProyeccionCarteleria";
import { convertirMontoCarteleriaPorFecha } from "./convertirMontoCarteleria";
import { esMesHistorico } from "./corteHistorico";
import { detalleModalCarteleriaHistorico } from "./historico/proyeccionModalHistorico";

const pad2 = (n) => String(n).padStart(2, "0");

const nombreReferenciaDe = (n, edificios, clientes) => {
  if (n.referencia_id && edificios[n.referencia_id]) return edificios[n.referencia_id]?.edificio;
  if (n.referencia_id && clientes[n.referencia_id]) {
    return clientes[n.referencia_id]?.empresa || clientes[n.referencia_id]?.nombre;
  }
  return `ID ${n.referencia_id || n.id}`;
};

// ── HARDWARE ──────────────────────────────────────────────────────────────────────────────────────
const itemsHardware = ({ negocios, isCierres, ano, start, end, pais, edificios, clientes }, hoy) =>
  negocios
    .filter((n) => n.incluye_hardware && !n.eliminada && ESTADOS_CIERRE.includes(n.estado))
    .map((n) => {
      const cal = calendarioNegocio(n, { hoy });
      let nombreReferencia = `ID ${n.referencia_id || n.id}`;
      if (n.referencia_id && edificios[n.referencia_id]) {
        nombreReferencia = edificios[n.referencia_id]?.edificio;
      } else if (n.referencia_id && clientes[n.referencia_id]) {
        nombreReferencia = clientes[n.referencia_id]?.empresa || clientes[n.referencia_id]?.nombre;
      }

      const monedaHW = (cal.monedas.hardware || "UF").toUpperCase();
      const esUF = monedaHW.includes("UF");

      let importeMesOriginal = 0;
      let importeMesConvertido = 0;
      let incluyeEnMes = false;
      const fechaConversion = new Date(ano, end, 0).toISOString().split("T")[0];
      const hw = cal.hardwareCapex; // evento CAPEX del motor (fecha_cierre, monto, tramos)

      if (isCierres) {
        const fecha = new Date(n.fecha_cierre);
        if (!isNaN(fecha)) {
          const mesNegocio = fecha.getMonth() + 1;
          const anoNegocio = fecha.getFullYear();
          if (anoNegocio === ano && mesNegocio >= start && mesNegocio <= end) {
            incluyeEnMes = true;
            importeMesOriginal = Number(n.monto_hardware) || 0;
            importeMesConvertido = convertirMontoCarteleriaPorFecha(
              importeMesOriginal,
              monedaHW,
              pais,
              fechaConversion
            );
          }
        }
      } else {
        // Detalle del modal Hardware: mes de FACTURACIÓN del tramo, cascada facturafecha/fecha_factura →
        // fecha_factura_estimada → fecha_cierre (los tramos normalizados del motor traen exactamente esa cascada).
        const tieneDesglose =
          n.modalidad === "CAPEX" &&
          Array.isArray(n.detalle_pagos_hardware) &&
          n.detalle_pagos_hardware.length > 0 &&
          !!hw;

        if (tieneDesglose) {
          const pagosMes = hw.tramos
            .map((t) => {
              const fechaTramo = t.facturafecha || t.fechaFacturaEstimada || n.fecha_cierre;
              const montoCargado = n.detalle_pagos_hardware[t.indice] ? n.detalle_pagos_hardware[t.indice].monto : null;
              // D12: un tramo ÚNICO sin monto vale `monto_hardware` (el motor ya lo resuelve en montoEfectivo).
              // Un tramo sin monto dentro de varios sigue descartado, como antes.
              const monto = montoCargado == null ? (t.montoEfectivo > 0 ? t.montoEfectivo : null) : t.monto;
              return { t, fechaTramo, monto };
            })
            .filter(({ fechaTramo, monto }) => {
              if (!fechaTramo || monto == null) return false;
              const fecha = new Date(fechaTramo);
              const mes = fecha.getMonth() + 1;
              const añoPago = fecha.getFullYear();
              return añoPago === ano && mes >= start && mes <= end;
            });
          if (pagosMes.length > 0) {
            incluyeEnMes = true;
            importeMesOriginal = pagosMes.reduce((sum, { monto }) => sum + (Number(monto) || 0), 0);
            importeMesConvertido = pagosMes.reduce((sum, { t, monto }) => {
              const fechaPago = (t.facturafecha || "").split("T")[0] || fechaConversion;
              return sum + convertirMontoCarteleriaPorFecha(Number(monto) || 0, monedaHW, pais, fechaPago);
            }, 0);
          }
        } else if (n.modalidad === "CAPEX" && n.fecha_cierre) {
          const fecha = new Date(n.fecha_cierre);
          const mes = fecha.getMonth() + 1;
          const añoFecha = fecha.getFullYear();
          if (añoFecha === ano && mes >= start && mes <= end) {
            incluyeEnMes = true;
            importeMesOriginal = Number(n.monto_hardware) || 0;
            importeMesConvertido = convertirMontoCarteleriaPorFecha(
              importeMesOriginal,
              monedaHW,
              pais,
              fechaConversion
            );
          }
        } else if (n.modalidad === "OPEX" && n.cuota_hardware_opex && n.fecha_cierre) {
          // OPEX: el calendario de cuotas del modal arranca en `fecha_cierre` (no es el devengo mensual del motor).
          const fechaCierre = new Date(n.fecha_cierre);
          const mesCierre = fechaCierre.getMonth();
          const añoCierre = fechaCierre.getFullYear();
          const totalCuotas = Number(n.num_cuotas) || cal.plazo || 36;

          for (let i = 0; i < totalCuotas; i++) {
            const mesTotal = mesCierre + i;
            const añoActual = añoCierre + Math.floor(mesTotal / 12);
            const mesActual = mesTotal % 12;
            if (añoActual === ano && mesActual + 1 >= start && mesActual + 1 <= end) {
              incluyeEnMes = true;
              importeMesOriginal = Number(n.cuota_hardware_opex) * cal.pantallas;
              importeMesConvertido = convertirMontoCarteleriaPorFecha(
                importeMesOriginal,
                monedaHW,
                pais,
                fechaConversion
              );
              break;
            }
          }
        }
      }

      return {
        ...n,
        esBTL: false,
        esCarteleria: false,
        esHardware: true,
        cliente: nombreReferencia,
        importe: importeMesConvertido,
        importeOriginal: importeMesOriginal,
        monedaOriginal: monedaHW,
        esMonedaUF: esUF,
        incluyeEnMes,
      };
    })
    .filter((item) => item.incluyeEnMes);

// ── CARTELERÍA: cierres ───────────────────────────────────────────────────────────────────────────
const itemsCarteleriaCierres = ({ negocios, ano, start, end, pais, edificios, clientes }, hoy) => {
  const todas = [];
  const fechaConversion = new Date(ano, end, 0).toISOString().split("T")[0];
  negocios.forEach((n) => {
    if (n.eliminada) return;
    if (!ESTADOS_CIERRE.includes(n.estado)) return;

    const cal = calendarioNegocio(n, { hoy });
    const c = componentesCierreCarteleria(n, cal, "UF");
    const esUF = c.monedaGestion.includes("UF") || c.monedaContenidos.includes("UF");
    const nombreReferencia = nombreReferenciaDe(n, edificios, clientes);

    // Cierre base (gestión + contenidos si no hay rango propio)
    if (n.fecha_cierre) {
      const fecha = new Date(n.fecha_cierre + "T00:00:00");
      if (!isNaN(fecha)) {
        const mesNegocio = fecha.getMonth() + 1;
        if (fecha.getFullYear() === ano && mesNegocio >= start && mesNegocio <= end) {
          const montoMensualOriginal = (c.feeGestion + c.feeContenidosEnBase) * c.pantallas;
          todas.push({
            ...n,
            esBTL: false,
            esCarteleria: true,
            esHardware: false,
            cliente: nombreReferencia,
            importe: totalCierreBaseLocal(c, pais, fechaConversion),
            importeOriginal: montoMensualOriginal * c.plazo,
            monedaOriginal: c.monedaGestion,
            esMonedaUF: esUF,
            pantallas: c.pantallas,
            plazo: c.plazo,
            feeGestion: c.feeGestion,
            feeContenidos: c.feeContenidosEnBase,
          });
        }
      }
    }

    // Cierre adicional de contenidos con rango propio
    if (c.tieneRangoPropio) {
      const fechaCont = new Date(n.contenidos_fecha_inicio + "T00:00:00");
      if (!isNaN(fechaCont)) {
        const mesCont = fechaCont.getMonth() + 1;
        if (fechaCont.getFullYear() === ano && mesCont >= start && mesCont <= end) {
          const plazoCont = plazoContenidosPropio(n, c.plazo);
          const contenidosLocal = convertirMontoCarteleriaPorFecha(
            c.feeContenidos * c.pantallas,
            c.monedaContenidos,
            pais,
            fechaConversion
          );
          const montoMensualOriginal = c.feeContenidos * c.pantallas;
          todas.push({
            ...n,
            id: `${n.id}-contenidos`,
            esBTL: false,
            esCarteleria: true,
            esHardware: false,
            cliente: `${nombreReferencia} (contenidos)`,
            importe: contenidosLocal * plazoCont,
            importeOriginal: montoMensualOriginal * plazoCont,
            monedaOriginal: c.monedaContenidos,
            esMonedaUF: c.monedaContenidos.includes("UF"),
            pantallas: c.pantallas,
            plazo: plazoCont,
            feeGestion: 0,
            feeContenidos: c.feeContenidos,
          });
        }
      }
    }
  });
  return todas;
};

// ── CARTELERÍA: facturación mensual (devengo del motor) ───────────────────────────────────────────
const itemsCarteleriaFacturacion = ({ negocios, ano, end, pais, edificios, clientes }, hoy) => {
  const claveMes = `${ano}-${pad2(end)}`;
  const fechaConversion = new Date(ano, end, 0).toISOString().split("T")[0];
  return negocios
    .filter((n) => !n.eliminada && ESTADOS_CIERRE.includes(n.estado) && n.fecha_inicio)
    .map((n) => ({ n, cal: calendarioNegocio(n, { hoy }) }))
    .filter(({ cal }) => {
      if (!(cal.plazo > 0)) return false;
      // Ventana del motor: desde fecha_inicio, `mesesVentana` meses (plazo firmado; el activo vencido, hasta hoy).
      const [yI, mI] = cal.fechaInicio.split("T")[0].split("-").map(Number);
      const idx = (ano - yI) * 12 + (end - mI);
      return idx >= 0 && idx < cal.mesesVentana;
    })
    .map(({ n, cal }) => {
      const pantallas = cal.pantallas;
      const monedaGestion = (cal.monedas.gestion || "UF").toUpperCase();
      const monedaContenidos = (cal.monedas.contenidosCargada || "UF").toUpperCase();
      const esUF = monedaGestion.includes("UF") || monedaContenidos.includes("UF");

      const dg = devengoDelMes(cal, "gestion", claveMes);
      const dc = devengoDelMes(cal, "contenidos", claveMes); // null fuera de la ventana de contenidos
      const gestionDevengada = dg ? dg.monto : 0;
      const contenidosDevengados = dc ? dc.monto : 0;
      const gestion = cal.porPantalla.gestion.monto;
      const contenidos = dc ? cal.porPantalla.contenidos.monto : 0;

      const gestionCLP = convertirMontoCarteleriaPorFecha(gestionDevengada, monedaGestion, pais, fechaConversion);
      const contenidosCLP = convertirMontoCarteleriaPorFecha(
        contenidosDevengados,
        monedaContenidos,
        pais,
        fechaConversion
      );
      // Modo legado: el devengo es el fee completo → (gestión + contenidos) × pantallas, como antes.
      const montoMensualOriginal =
        cal.modo === "legado" ? (gestion + contenidos) * pantallas : gestionDevengada + contenidosDevengados;

      return {
        ...n,
        esBTL: false,
        esCarteleria: true,
        esHardware: false,
        cliente: nombreReferenciaDe(n, edificios, clientes),
        importe: gestionCLP + contenidosCLP,
        importeOriginal: montoMensualOriginal,
        monedaOriginal: monedaGestion,
        esMonedaUF: esUF,
        pantallas,
        plazo: cal.plazo,
        feeGestion: gestion,
        feeContenidos: contenidos,
      };
    });
};

/**
 * Ítems de Hardware + Cartelería del modal de detalle de la Proyección de ventas (un mes: start === end).
 * @param {{negocios: object[], vertical: (string|null), isCierres: boolean, ano: number, start: number,
 *   end: number, pais: string, edificios: object, clientes: object}} args mismos valores que usaba el bloque inline
 * @param {Date} [hoy] reloj para el motor (default: hoy real)
 */
export const detalleProyeccionCarteleria = (args, hoy = new Date()) => {
  // El modal abre un solo mes; si algún día recibiera un rango, manda su último mes.
  if (esMesHistorico(args.ano, args.end - 1)) return detalleModalCarteleriaHistorico(args);
  const { vertical, isCierres } = args;
  let todas = [];
  if (vertical === "hardware" || vertical === null) todas = [...todas, ...itemsHardware(args, hoy)];
  if (vertical === "carteleria" || vertical === null) {
    todas = [...todas, ...(isCierres ? itemsCarteleriaCierres(args, hoy) : itemsCarteleriaFacturacion(args, hoy))];
  }
  return todas;
};
